import classes from './stroke-trace.module.css';

/*
 * A hand writing itself onto ruled paper. Abstract on purpose — deliberately not
 * legible characters, and not a claim about any real student's data. Strokes draw
 * in reading order, blue dots mark pauses, one line is struck through and rewritten.
 */

/*
 * Irregular amplitudes so the trace reads as handwriting rather than a sine wave:
 * mostly small arcs above the baseline, with the occasional taller ascender and
 * a shallower dip below. Fixed table, so every render draws the same hand.
 */
const AMPS = [8, 5, 12, 6, 9, 4, 7, 14, 5, 8, 6, 11, 4, 9, 7, 5];

const word = (x, y, len, seed = 0) => {
    const step = 9.5;
    const n = Math.max(2, Math.round(len / step));
    let d = `M${x} ${y}`;
    for (let i = 0; i < n; i += 1) {
        const a = AMPS[(i + seed * 3) % AMPS.length];
        // up-arcs dominate; every third arc dips below the line like a descender
        const dy = i % 3 === 2 ? a * 0.42 : -a;
        d += ` c 2.5 ${dy.toFixed(1)}, 7 ${dy.toFixed(1)}, ${step} 0`;
    }
    return d;
};

// [x, length] per word — gaps of ~16px so words separate at a glance
const LINES = [
    { y: 78, words: [[52, 84], [152, 56]] },
    { y: 120, words: [[52, 48], [116, 76], [208, 38]] },
    { y: 162, words: [[52, 110]], struck: [48, 118] },
    { y: 204, words: [[52, 68], [136, 92]] },
    { y: 246, words: [[52, 56]], caret: 116 },
];

const PAUSES = [
    { cx: 216, cy: 78 },
    { cx: 170, cy: 162 },
    { cx: 236, cy: 204 },
];

const StrokeTrace = () => {
    let order = 0;

    return (
        <div className={classes.slate}>
            <svg
                className={classes.trace}
                viewBox="0 0 340 290"
                role="img"
                aria-label="An abstract trace of a student working by hand: strokes appearing line by line, pauses, and one line struck through and written again."
            >
                <g className={classes.rules} aria-hidden="true">
                    {LINES.map((l) => (
                        <line key={l.y} x1="44" y1={l.y + 6} x2="300" y2={l.y + 6} />
                    ))}
                </g>

                <g className={classes.strokes}>
                    {LINES.map((l) =>
                        l.words.map(([x, len]) => {
                            order += 1;
                            return (
                                <path
                                    key={`${l.y}-${x}`}
                                    d={word(x, l.y, len, order)}
                                    style={{ animationDelay: `${0.5 + order * 0.26}s` }}
                                />
                            );
                        })
                    )}
                </g>

                {/* the correction — a line ruled through work that didn't hold */}
                {LINES.filter((l) => l.struck).map((l) => (
                    <line
                        key={`s-${l.y}`}
                        className={classes.struck}
                        x1={l.struck[0]}
                        y1={l.y + 2}
                        x2={l.struck[0] + l.struck[1]}
                        y2={l.y - 2}
                    />
                ))}

                {/* the pen, still on the page */}
                {LINES.filter((l) => l.caret).map((l) => (
                    <line
                        key={`c-${l.y}`}
                        className={classes.caret}
                        x1={l.caret}
                        y1={l.y - 12}
                        x2={l.caret}
                        y2={l.y + 10}
                    />
                ))}

                <g className={classes.pauses} aria-hidden="true">
                    {PAUSES.map((p, i) => (
                        <circle
                            key={p.cx}
                            cx={p.cx}
                            cy={p.cy}
                            r="3.6"
                            style={{ animationDelay: `${1.6 + i * 1.15}s` }}
                        />
                    ))}
                </g>
            </svg>

            <p className={classes.caption}>
                <span>stroke</span>
                <span className={classes.dot} aria-hidden="true">·</span>
                <span>pause</span>
                <span className={classes.dot} aria-hidden="true">·</span>
                <span>erase</span>
                <span className={classes.dot} aria-hidden="true">·</span>
                <span>try again</span>
            </p>
        </div>
    );
};

export default StrokeTrace;
